import React, { useState } from 'react';
import axios from 'axios';
import { Edit2, Trash2, Plus, Save, X } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useToast } from '../../components/ui/Toast';
import { slugify, getCategorySlug } from '../../utils/slugify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';


const CategoriesCRUD = ({ categories, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    category_type: 'general',
    slug: ''
  });

  const categoryTypeOptions = [
    { value: 'general', label: 'General (not shown in nav menus)' },
    { value: 'solution_accelerators', label: 'By Business Solutions' },
    { value: 'engineering_utilities', label: 'By Engineering Toolkits & Framework' },
  ];

  const getCategoryTypeLabel = (type) =>
    categoryTypeOptions.find((option) => option.value === type)?.label || type;

  // Initialize toast and escape key hook
  const { toast } = useToast();
  useEscapeKey(() => setShowModal(false), showModal);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setSlugManuallyEdited(false);
    setFormData({ name: '', description: '', icon: '', category_type: 'general', slug: '' });
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setSlugManuallyEdited(true);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      icon: category.icon || '',
      category_type: category.category_type || 'general',
      slug: category.slug || ''
    });
    setShowModal(true);
  };

  const handleNameChange = (name) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : slugify(name)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        category_type: formData.category_type || 'general',
      };

      if (editingCategory) {
        const categoryId = editingCategory.id;
        if (!categoryId) {
          toast.error('Category ID is missing. Please refresh and try again.');
          return;
        }
        await axios.put(`${API_BASE_URL}/api/admin/categories/${categoryId}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Category updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/categories`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Category created successfully!');
      }
      setShowModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error saving category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    const categoryId = category?.id;
    if (!categoryId) {
      toast.error('Category ID is missing. Please refresh and try again.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this category? This will also delete all related subcategories and agents.')) {
      setIsSubmitting(true);
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/categories/${categoryId}`, {
          headers: getAuthHeaders()
        });
        toast.success('Category deleted successfully!');
        onUpdate();
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Error deleting category. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categories Management</h2>
          <p className="text-gray-600">Organize your AI agents into categories</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <span>+</span>
          <span>Add Category</span>
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{category.icon}</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-600">{category.description}</p>
                  <p className="text-xs text-gray-400 mt-1">{getCategoryTypeLabel(category.category_type)}</p>
                  <p className="text-xs text-gray-400">/{getCategorySlug(category)}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => openEditModal(category)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(category)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              Created: {new Date(category.created_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug (optional)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true);
                      setFormData({ ...formData, slug: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder={editingCategory ? getCategorySlug(editingCategory) : 'auto-generated-from-name'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Browse URL path: /{formData.slug ? slugify(formData.slug) : (formData.name ? slugify(formData.name) : 'category-slug')}
                  </p>
                  <p className="text-xs text-gray-500">
                    Leave blank to auto-generate from the category name.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category Type (optional)</label>
                  <select
                    value={formData.category_type || 'general'}
                    onChange={(e) => setFormData({ ...formData, category_type: e.target.value || 'general' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {categoryTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Defaults to General if not changed. Determines which top navigation menu this category appears under.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({...formData, icon: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="🏠"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{editingCategory ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      <span>{editingCategory ? 'Update' : 'Create'} Category</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default CategoriesCRUD;
